import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SITE_URL = window.location.origin;

/**
 * RouteSEO — fetches per-page meta tags from DB based on current route.
 * Wraps the SEO component with page-specific overrides.
 * Caches results to avoid refetching on every navigation.
 */
// Helper — read cached meta_title for a given pathname from localStorage.
// Lets Helmet render the correct title on first React mount, before the
// async API fetch returns — so the tab title doesn't flicker through the
// global site_name in between.
const readCachedMeta = (pathname) => {
  try {
    const cache = JSON.parse(localStorage.getItem('pageMetaCache') || '{}');
    const title = cache[pathname];
    return title ? { meta_title: title } : null;
  } catch (e) { return null; }
};

const RouteSEO = ({ globalSettings }) => {
  const location = useLocation();
  // Initialize from localStorage so first render uses the cached route title.
  const [pageMeta, setPageMeta] = useState(() => readCachedMeta(location.pathname));
  const cacheRef = useRef({});

  useEffect(() => {
    const pathname = location.pathname;

    // Skip fetching for admin routes AND for app modules that own their own
    // title via their own React component (e.g., /translation reads from
    // translation_page_settings and sets document.title itself). SEO module is
    // for crawlable pages only — anything behind login isn't seen by crawlers
    // and doesn't need Helmet meta tags. As each app module migrates to
    // owning its own title, it gets added to this list.
    if (
      pathname.includes('/admin') ||
      pathname.includes('/login') ||
      pathname.includes('/register') ||
      pathname === '/translation'
    ) {
      setPageMeta(null);
      return;
    }

    // Apply cached meta immediately on route change so navigation doesn't
    // flicker through the global title while the API fetch is in flight.
    const cached = readCachedMeta(pathname);
    if (cached) setPageMeta(cached);

    // Check in-memory cache (already-fetched full meta object)
    if (cacheRef.current[pathname] !== undefined) {
      setPageMeta(cacheRef.current[pathname]);
      return;
    }

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${backendUrl}/api/seo/page-meta?path=${encodeURIComponent(pathname)}`)
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        // API returns null if no page-specific meta exists
        const meta = data && data.meta_title ? data : null;
        cacheRef.current[pathname] = meta;
        setPageMeta(meta);
        // Persist meta_title in localStorage by pathname so the next page load
        // can restore the correct tab title BEFORE React mounts — kills the
        // flicker between static index.html title and the DB-set route title.
        try {
          if (meta && meta.meta_title) {
            const cache = JSON.parse(localStorage.getItem('pageMetaCache') || '{}');
            cache[pathname] = meta.meta_title;
            localStorage.setItem('pageMetaCache', JSON.stringify(cache));
          }
        } catch (e) {}
      })
      .catch(() => {
        cacheRef.current[pathname] = null;
        setPageMeta(null);
      });
  }, [location.pathname]);

  // Private pages and app modules that own their own title — no SEO render.
  // Lets index.html's static title remain unchanged (no React Helmet swap)
  // until the module's React component sets document.title itself.
  // Keep this list in sync with the useEffect skip-list above.
  const pathname = location.pathname;
  if (
    pathname.includes('/admin') ||
    pathname.includes('/login') ||
    pathname.includes('/register') ||
    pathname === '/translation'
  ) {
    return null;
  }

  if (!globalSettings) return null;

  return (
    <SEO
      title={globalSettings.site_name || undefined}
      description={globalSettings.site_description || undefined}
      keywords={globalSettings.site_keywords || undefined}
      favicon={globalSettings.default_og_image || undefined}
      image={globalSettings.default_og_image || undefined}
      googleAnalyticsId={globalSettings.google_analytics_id || undefined}
      googleSearchConsoleId={globalSettings.google_search_console_verification || undefined}
      pageMeta={pageMeta}
    />
  );
};

const SEO = ({
  title = "Darija Pro - Learn Moroccan Arabic Online | Interactive Games & Translation",
  description = "Master Moroccan Darija with Darija Pro. Free interactive games, instant translation, daily lessons & conversation practice. Start learning Arabic dialect today!",
  keywords = "learn darija, moroccan arabic, darija translation, learn moroccan dialect, arabic games, darija lessons, moroccan language app, learn arabic online, darija pro",
  image,
  url,
  type = "website",
  favicon,
  siteName = "Darija Pro",
  socialLinks = [],
  googleAnalyticsId,
  googleSearchConsoleId,
  pageMeta
}) => {
  // Per-page meta overrides global defaults
  const finalTitle = pageMeta?.meta_title || title;
  const finalDescription = pageMeta?.meta_description || description;
  const finalKeywords = pageMeta?.meta_keywords || keywords;
  const finalOgTitle = pageMeta?.og_title || finalTitle;
  const finalOgDescription = pageMeta?.og_description || finalDescription;
  const finalOgImage = pageMeta?.og_image || image || `${SITE_URL}/og-image.png`;
  const finalCanonical = pageMeta?.canonical_url || url || `${SITE_URL}${window.location.pathname}`;
  const finalRobots = pageMeta?.robots || "index, follow";
  const finalTwitterTitle = pageMeta?.twitter_title || finalOgTitle;
  const finalTwitterDescription = pageMeta?.twitter_description || finalOgDescription;
  const finalTwitterImage = pageMeta?.twitter_image || finalOgImage;

  return (
    <Helmet>
      {/* Dynamic Favicon */}
      {favicon && <link rel="icon" href={favicon} />}
      {favicon && <link rel="apple-touch-icon" href={favicon} />}

      {/* Primary Meta Tags — only override the static index.html title when we
          actually have a per-route meta_title (from API fetch or localStorage
          cache). Otherwise Helmet would briefly render globalSettings.site_name
          as the title, causing a visible flash on every page load. */}
      {pageMeta?.meta_title && <title>{pageMeta.meta_title}</title>}
      {pageMeta?.meta_title && <meta name="title" content={pageMeta.meta_title} />}
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />

      {/* Language */}
      <html lang="en" />
      <meta name="language" content="English" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={finalCanonical} />
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:image" content={finalOgImage} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={finalCanonical} />
      <meta property="twitter:title" content={finalTwitterTitle} />
      <meta property="twitter:description" content={finalTwitterDescription} />
      <meta property="twitter:image" content={finalTwitterImage} />

      {/* Robots */}
      <meta name="robots" content={finalRobots} />
      <meta name="googlebot" content={finalRobots} />
      <meta name="author" content={siteName} />
      <meta name="publisher" content={siteName} />

      {/* Google Search Console Verification */}
      {googleSearchConsoleId && (
        <meta name="google-site-verification" content={googleSearchConsoleId} />
      )}

      {/* Canonical URL */}
      <link rel="canonical" href={finalCanonical} />

      {/* Mobile */}
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
      <meta name="theme-color" content="#8b5cf6" />

      {/* PWA */}
      <link rel="manifest" href="/manifest.json" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content={siteName} />

      {/* Google Analytics 4 */}
      {googleAnalyticsId && (
        <script async src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`} />
      )}
      {googleAnalyticsId && (
        <script>{`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${googleAnalyticsId}');
        `}</script>
      )}

      {/* Structured Data - Organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "EducationalOrganization",
          "name": siteName,
          "description": finalDescription,
          "url": SITE_URL,
          "logo": `${SITE_URL}/logo512.png`,
          "sameAs": socialLinks.length > 0 ? socialLinks : undefined,
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "Customer Support",
            "availableLanguage": ["English", "Arabic", "French"]
          }
        })}
      </script>

      {/* Structured Data - Website */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": siteName,
          "alternateName": "Learn Darija Online",
          "url": SITE_URL,
          "potentialAction": {
            "@type": "SearchAction",
            "target": `${SITE_URL}/translation?q={search_term_string}`,
            "query-input": "required name=search_term_string"
          }
        })}
      </script>

      {/* Structured Data - WebApplication */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": siteName,
          "applicationCategory": "EducationalApplication",
          "operatingSystem": "Any",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          }
        })}
      </script>

      {/* Per-page schema markup from DB */}
      {pageMeta?.schema_markup && (
        <script type="application/ld+json">
          {pageMeta.schema_markup}
        </script>
      )}
    </Helmet>
  );
};

export { RouteSEO };
export default SEO;
